import { useState } from 'react';
import { toast } from 'sonner';
import { Check, Facebook, Link2, Linkedin, Mail } from 'lucide-react';
import { useLang } from '../i18n';

const X = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" width="14" height="14" {...props}>
    <path d="M18.24 2.25h3.31l-7.23 8.26 8.5 11.24h-6.63l-5.2-6.8-5.94 6.8H1.73l7.5-8.58L1.07 2.25h6.8l4.71 6.23 5.66-6.23Zm-1.16 17.52h1.83L7.01 4.13H5.05l12.03 15.64Z" />
  </svg>
);

const Whatsapp = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" width="15" height="15" {...props}>
    <path d="M12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 1.75.46 3.46 1.32 4.96L2 22l5.25-1.38a9.9 9.9 0 0 0 4.79 1.22h.01c5.46 0 9.91-4.45 9.91-9.91C21.96 6.45 17.5 2 12.04 2Zm5.8 14.03c-.24.68-1.4 1.3-1.93 1.35-.53.05-1.02.24-3.5-.73-2.98-1.17-4.86-4.25-5-4.45-.15-.2-1.19-1.6-1.19-3.05 0-1.45.76-2.16 1.03-2.46.27-.29.58-.37.78-.37.2 0 .39 0 .56.01.19.01.44-.07.68.53.24.58.83 2.03.9 2.18.07.15.12.32.02.51-.1.2-.15.32-.29.49-.15.17-.31.39-.44.53-.15.15-.3.31-.13.61.17.29.75 1.24 1.61 2.01 1.11.99 2.04 1.3 2.34 1.45.29.15.46.12.63-.07.17-.2.73-.85.93-1.14.19-.29.39-.24.66-.15.27.1 1.71.81 2 .96.29.15.49.22.56.34.07.12.07.7-.17 1.38Z" />
  </svg>
);

const Pinterest = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" width="15" height="15" {...props}>
    <path d="M12.02 2C6.9 2 4 5.32 4 8.93c0 1.68.9 3.77 2.35 4.43.68.31.6-.07.71-.63.09-.44.05-.24.14-.66.05-.2.02-.29-.13-.47a3.62 3.62 0 0 1-.71-2.14c0-2.79 2.11-5.29 5.49-5.29 2.99 0 4.6 1.83 4.6 4.27 0 3.21-1.42 5.93-3.53 5.93-1.16 0-2.03-.96-1.75-2.14.33-1.41.98-2.93.98-3.95 0-.91-.49-1.67-1.5-1.67-1.19 0-2.15 1.23-2.15 2.88 0 1.05.36 1.76.36 1.76l-1.42 6.03c-.24 1.02-.04 2.7-.02 2.85.02.08.11.1.16.04.07-.09.96-1.42 1.26-2.42l.72-2.65c.38.72 1.48 1.32 2.66 1.32 3.48 0 5.99-3.2 5.99-7.17C20.36 5.28 17.13 2 12.02 2Z" />
  </svg>
);

const Btn = ({ label, testid, onClick, href, children }) => {
  const cls =
    'group relative flex h-11 w-11 items-center justify-center border border-inchiostro/16 text-inchiostro/60 transition-[color,background-color,border-color,transform] duration-300 hover:-translate-y-0.5 hover:border-marsala hover:bg-marsala hover:text-carta';
  const inner = (
    <>
      {children}
      <span className="pointer-events-none absolute -bottom-7 left-1/2 -translate-x-1/2 whitespace-nowrap text-[14px] font-bold text-inchiostro/70 opacity-0 transition-opacity duration-300 group-hover:opacity-100">
        {label}
      </span>
    </>
  );
  if (href) {
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={label}
        data-testid={testid}
        className={cls}
      >
        {inner}
      </a>
    );
  }
  return (
    <button type="button" onClick={onClick} aria-label={label} data-testid={testid} className={cls}>
      {inner}
    </button>
  );
};

/** Condivisione sobria: solo intent URL, nessuno script di terze parti, nessun tracciamento. */
export const ShareBar = ({ url, title, image, testid = 'product-share', className = 'mt-10 pt-6' }) => {
  const { t } = useLang();
  const [done, setDone] = useState(false);
  const u = encodeURIComponent(url);
  const txt = encodeURIComponent(title);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      const el = document.createElement('textarea');
      el.value = url;
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      el.remove();
    }
    setDone(true);
    toast(t('common.linkCopied'));
    setTimeout(() => setDone(false), 2200);
  };

  return (
    <div data-testid={testid} className={className}>
      <p className="label-wide mb-5 text-inchiostro/65">{t('common.share')}</p>
      <div className="flex flex-wrap items-center gap-3">
        <Btn
          label="Facebook"
          testid="share-facebook"
          href={`https://www.facebook.com/sharer/sharer.php?u=${u}`}
        >
          <Facebook size={15} strokeWidth={1.8} />
        </Btn>
        <Btn label="X" testid="share-x" href={`https://twitter.com/intent/tweet?url=${u}&text=${txt}`}>
          <X />
        </Btn>
        <Btn
          label="Pinterest"
          testid="share-pinterest"
          href={`https://pinterest.com/pin/create/button/?url=${u}&description=${txt}${
            image ? `&media=${encodeURIComponent(image)}` : ''
          }`}
        >
          <Pinterest />
        </Btn>
        <Btn label="WhatsApp" testid="share-whatsapp" href={`https://wa.me/?text=${txt}%20${u}`}>
          <Whatsapp />
        </Btn>
        <Btn
          label="LinkedIn"
          testid="share-linkedin"
          href={`https://www.linkedin.com/sharing/share-offsite/?url=${u}`}
        >
          <Linkedin size={15} strokeWidth={1.8} />
        </Btn>
        <Btn label="Email" testid="share-email" href={`mailto:?subject=${txt}&body=${u}`}>
          <Mail size={15} strokeWidth={1.8} />
        </Btn>
        <Btn label={t('common.copyLink')} testid="share-copy" onClick={copy}>
          {done ? <Check size={15} strokeWidth={2} /> : <Link2 size={15} strokeWidth={1.8} />}
        </Btn>
      </div>
    </div>
  );
};
